import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useStore } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { PROFILE } from '../../router/routes';
import { RootState } from '../../store/store';
import User from '../../interfaces/user';
import { useService } from '../../services/services';
import Form, { SubmitType } from '../../components/form';
import Disabled from '../../components/form/elements/disabled';
import Text from '../../components/form/elements/text';
import Group from '../../components/form/elements/group';
import Button from '../../components/form/elements/button';
import Link from '../../components/form/elements/link';
import { user } from '../../store/global-slice';
import { switchMap, tap } from 'rxjs/operators';

export interface ChangeEmailData extends User {
  new_email: string;
  new_email_confirmation: string;
}

export default function ChangeEmail() {
  const { profileService, authService } = useService();
  const history = useHistory();
  const { t } = useTranslation();
  const { dispatch } = useStore();
  const { userData } = useSelector((state: RootState) => ({
    userData: state.global.user,
  }));

  const [emailFormSchema] = useState(() =>
    yup.object().shape({
      new_email: yup.string().email().required(),
      new_email_confirmation: yup
        .string()
        .email()
        .required()
        .oneOf(
          [yup.ref('new_email')],
          t('pages.profile.messages.new_email_confirmation_error')
        ),
    })
  );

  const [bodyElements] = useState(() => [
    Group([
      Disabled([Text('name'), Text('email')]),
      Text('new_email', 'pages.profile.fields.new_email'),
      Text(
        'new_email_confirmation',
        'pages.profile.fields.new_email_confirmation'
      ),
    ]),
  ]);

  const [footerElements] = useState(() => [
    Button('save', null, 'change'),
    Link(PROFILE, 'cancel'),
  ]);

  const submit = useCallback<SubmitType<ChangeEmailData, User>>(
    (form) => [
      profileService
        .changeEmail(form.new_email, form.new_email_confirmation)
        .pipe(
          switchMap(() => {
            return authService.user().pipe(
              tap((userResponse) => {
                dispatch(user(userResponse)); // Refresh stored user.
              })
            );
          })
        ),
      () => {
        history.push(PROFILE);
      },
    ],
    [profileService, history, authService, dispatch]
  );

  return (
    <Form
      mode={'create'}
      title={t('pages.profile.title')}
      backTo={null}
      body={bodyElements}
      footer={footerElements}
      values={userData}
      submit={submit}
      schema={emailFormSchema}
    />
  );
}
